import React from 'react';
import { useField } from 'formik';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Box,
  InputLabel,
  StandardTextFieldProps,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import FormikContext from './FormikContext';

interface iFormikDatePicker extends StandardTextFieldProps {
  name: string;
  label?: string;
  native?: boolean;
  select?: boolean;
  maxHeight?: number | 'auto';
}

const MuiDatePicker = styled(DatePicker)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: 14,
    fontWeight: 500,
    '& fieldset': {
      borderColor: theme.palette.grey[300],
      boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.05)',
      borderWidth: 1,
      borderRadius: 8,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.grey[400],
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.grey[500],
      borderWidth: 1,
    },
  },

  '& .MuiFormHelperText-root': {
    color: theme.palette.error.main,
    fontSize: 12,
    fontWeight: 400,
    letterSpacing: 1,
    marginLeft: 0,
  },
}));

const FormikDatePicker: React.FC<iFormikDatePicker> = ({
  name,
  native,
  type,
  select,
  label,
  maxHeight,
  ...props
}) => {
  const { currentStep, steps, setSteps } = React.useContext(FormikContext);
  const [field, meta, helpers] = useField(name);

  const theme = useTheme();
  const medium = useMediaQuery(theme.breakpoints.up('md'));
  const space = medium ? 4 : 2;

  const { touched, error, value } = meta;
  const { onBlur } = field;

  // Set steps hasError: true if field has error
  React.useEffect(() => {
    if (steps && steps.length > 0) {
      if (touched && error) {
        const _steps = [...steps];
        _steps[currentStep.step].hasError = true;
        // setSteps(_steps);
        localStorage.setItem('steps', JSON.stringify(_steps));
      } else {
        const _steps = [...steps];
        _steps[currentStep.step].hasError = false;
        // setSteps(_steps);
        localStorage.setItem('steps', JSON.stringify(_steps));
      }
    }
  }, [error]);

  return (
    <Box mt={label ? space : 0}>
      <InputLabel
        htmlFor={name}
        sx={{
          fontSize: 14,
          fontWeight: 500,
          color: 'text.primary',
          mb: 1,
        }}
      >
        {label}
      </InputLabel>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDatePicker
          format="DD/MM/YYYY"
          onChange={(value) => helpers.setValue(value)}
          value={value}
          slotProps={{
            inputAdornment: {
              position: 'end',
            },
            textField: {
              fullWidth: true,
              size: 'small',
              variant: 'outlined',
              helperText: touched && error,
              error: touched && Boolean(error),
              onBlur,
              ...props,
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default FormikDatePicker;
